'use client';

import './lead-form.scss';

import * as z from 'zod';
import React, { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { generalConfig } from '@sola/config/lib/getters.ts';
import type { CountryCode } from '@sola/schema/dotcom/shared/geo.ts';
import { StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import type { FormDynamicSettings } from '@sola/schema/lib/schema.ts';
import type { LeadChurchRequest } from '@sola/schema/lib/skippy.ts';
import { trackFormSuccess } from '@sola/tracking/components/datalayer.tsx';
import { camelCaseToLabel } from '@sola/utils/string.ts';

import { NameFields } from '../../../molecules/name-fields/name-fields.tsx';
import { AssetLinksFooter } from '../asset-links.tsx';
import { parseInlineLinks } from '../parse-inline-links.tsx';

export type LeadFormProps = {
  onSubmit: (data: LeadChurchRequest) => Promise<boolean>;
  config?: Partial<FormDynamicSettings>;
};

const LeadFormState = z.object({
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional(),
  showThankYou: z.boolean().optional(),
});
type LeadFormState = z.infer<typeof LeadFormState>;

const makeOption = (value: string) => {
  return { text: camelCaseToLabel(value), value };
};

export const LeadForm: React.FC<LeadFormProps> = ({ onSubmit, config }) => {
  const { t } = useTranslation(['forms', 'skippy']);
  const [formState, setFormState] = useState<LeadFormState>({});
  const defaultCountry = 'US' as CountryCode;
  const shouldValidate = true;
  const roleOptions = [
    { text: t('skippy:form.roleOptions.defaultText'), value: '' },
    ...(config?.roles && config.roles.length > 0
      ? config.roles!
      : [
          t('skippy:form.roleOptions.pastor'),
          t('skippy:form.roleOptions.churchStaff'),
          t('skippy:form.roleOptions.congregant'),
        ]
    ).map(makeOption),
  ];

  const sizeOptions = [
    { text: t('skippy:form.sizeOptions.defaultText'), value: '' },
    ...[
      '1-49',
      '50-99',
      '100-199',
      '200-299',
      '300-399',
      '400-499',
      '500-749',
      '750-999',
      '1,000',
      '2,000',
      '3,000',
      '4,000',
      '5,000',
      '6,000',
      '7,000',
      '8,000',
      '9,000',
      '10,000+',
    ].map(makeOption),
  ];

  const stateOptions = [
    { text: t('skippy:form.stateOptions.defaultText'), value: '' },
    ...Object.entries(StateEnum).map(([abbr, name]) => ({
      text: name,
      value: abbr,
    })),
  ];

  const handleSubmit = async (data: FieldValues) => {
    let errorMessage = '';

    setFormState((prevState) => ({
      ...prevState,
      errorMessage,
      isSubmitting: true,
    }));

    try {
      const { code, quantity, ...rest } = data;
      const items =
        code && quantity != null
          ? [{ code: code as string, quantity: Number(quantity) }]
          : undefined;

      const payload = {
        ...rest,
        items,
      } as LeadChurchRequest;

      const success = await onSubmit(payload);
      if (success) {
        trackFormSuccess(config?.subscriptionCode ?? 'Lead Form');
        setFormState({
          showThankYou: true,
          isSubmitting: false,
        });
        return;
      }

      errorMessage = t('skippy:form.errorVerify');
    } catch {
      errorMessage = t('skippy:form.errorSave');
    }

    if (errorMessage) {
      setFormState({
        isSubmitting: false,
        errorMessage,
      });
    }
  };

  const selectItemsArray = (minValue?: string, maxValue?: string) => {
    const min =
      minValue && !Number.isNaN(Number(minValue))
        ? Number(minValue)
        : Number(t('forms:orderFields.kitQuantityMin'));
    const max =
      maxValue && !Number.isNaN(Number(maxValue))
        ? Number(maxValue)
        : Number(t('forms:orderFields.kitQuantityMax'));
    return Array.from({ length: max - min + 1 }, (_, index) => ({
      text: (min + index).toString(),
      value: (min + index).toString(),
    }));
  };

  if (formState.showThankYou) {
    const assets = config?.thankYouAssets;
    return (
      <Alert
        variant="positive"
        message={
          config?.messageThankYou ?? t('skippy:form.leadCreate.thankYou')
        }
        buttonAction={
          assets && assets.length > 0 ? (
            <AssetLinksFooter assets={assets} />
          ) : undefined
        }
        noIcon
        scrollBehavior="instant"
        scrollPosition="center"
        scrollIntoView
      />
    );
  }

  return (
    <div className="LeadForm w-full max-w-sm">
      {formState.errorMessage && (
        <div className="mb-comfy-xs">
          <Alert
            className="scroll-mt-roomy-md"
            variant="negative"
            message={formState.errorMessage}
            scrollIntoView
          />
        </div>
      )}

      <Form className="@sm:gap-comfy-lg" onSubmit={handleSubmit}>
        {config?.kitQuantity?.hide === false && config?.kitQuantityCode && (
          <>
            <Select
              fieldClasses="hidden"
              name="code"
              value={config.kitQuantityCode}
              items={[
                {
                  value: config.kitQuantityCode,
                  text: config.kitQuantityCode,
                },
              ]}
            />
            <Select
              label={
                config?.kitQuantityText ??
                t('forms:orderFields.kitQuantityText')
              }
              name="quantity"
              required={config?.kitQuantity?.required}
              items={selectItemsArray(
                config?.kitQuantityMin,
                config?.kitQuantityMax,
              )}
            />
          </>
        )}
        <Fieldset>
          <div className="grid grid-cols-1 @smc:grid-cols-2 gap-comfy-sm">
            <NameFields autoComplete={false} />
          </div>
          {!config?.role?.hide && (
            <Select
              label={t('forms:contactFields.roleLabel')}
              name="role"
              required={config?.role?.required}
              items={roleOptions}
            />
          )}
          <EmailField
            name="email"
            label={t('forms:contactFields.emailLabel')}
            required
          />
          {!config?.phone?.hide && (
            <PhoneField
              label={t('forms:contactFields.phoneNumberLabel')}
              name="phone"
              countryCode={defaultCountry}
              required={config?.phone?.required}
            />
          )}
        </Fieldset>
        {config?.hasChurch && (
          <Fieldset>
            <InputField
              label={t('forms:churchFields.churchNameLabel')}
              name="churchName"
              required
              maxLength={generalConfig.leadForms.churchNameMaxLength}
            />
            <InputField
              label={t('forms:churchFields.churchAddressLabel')}
              name="churchAddress"
              required
            />
            <InputField
              label={t('forms:churchFields.churchCityLabel')}
              name="churchCity"
              required
              maxLength={generalConfig.leadForms.churchCityMaxLength}
            />
            <div className="grid grid-cols-1 @smc:grid-cols-2 gap-comfy-sm">
              <StateField
                label={t('forms:churchFields.churchStateLabel')}
                name="churchState"
                autoComplete="address-level1"
                required
                items={stateOptions}
                isUS
              />

              <PostalCodeField
                label={t('forms:churchFields.churchPostalLabel')}
                name="churchPostal"
                autoComplete="postal-code"
                countryCode={defaultCountry}
                shouldValidate={shouldValidate}
                required
              />
            </div>

            <Select
              label={t('forms:churchFields.churchSizeLabel')}
              name="memberSize"
              required
              items={sizeOptions}
            />

            {!config?.website?.hide && (
              <InputField
                label={t('forms:churchFields.churchWebsiteLabel')}
                name="churchWebsite"
                required={config?.website?.required}
                type="url"
              />
            )}
          </Fieldset>
        )}
        {config?.hasShipping && (
          <Fieldset>
            <InputField
              label={t('forms:addressFields.addressLabel')}
              name="churchAddress"
              required
            />
            <InputField
              label={t('forms:addressFields.cityLabel')}
              name="churchCity"
              required
              maxLength={generalConfig.leadForms.churchCityMaxLength}
            />
            <div className="grid grid-cols-1 @smc:grid-cols-2 gap-comfy-sm">
              <StateField
                label={t('forms:addressFields.stateLabel')}
                name="churchState"
                autoComplete="address-level1"
                required
                items={stateOptions}
                isUS
              />

              <PostalCodeField
                label={t('forms:addressFields.zipCodeLabel')}
                name="churchPostal"
                autoComplete="postal-code"
                countryCode={defaultCountry}
                shouldValidate={shouldValidate}
                required
              />
            </div>
          </Fieldset>
        )}

        {!config?.comments?.hide && (
          // @TODO: textarea support
          <InputField
            label={t('forms:contactFields.commentsLabel')}
            name={
              config?.leadType === 'order' ? 'specialInstructions' : 'comments'
            }
            required={config?.comments?.required}
          />
        )}
        {config?.consent && (
          <div className="mt-comfy-xs">
            <Checkbox
              name="consent"
              label={config.consentText || t('forms:consentText')}
              labelContent={
                config.consentText
                  ? parseInlineLinks(config.consentText)
                  : undefined
              }
              required
            />
          </div>
        )}
        <FormSubmit
          className={cn({
            'mt-comfy-lg': !config?.consent,
            'mt-comfy-xs': !!config?.consent,
          })}
          isBusy={formState.isSubmitting}
          iconAfter={<ArkIcon iconName="arrow-right" />}
        >
          {config?.buttonText ?? t('skippy:form.leadCreate.buttonText')}
        </FormSubmit>
      </Form>
    </div>
  );
};
